import React, {
  useCallback, useEffect, useId, useImperativeHandle, useRef, useState,
} from 'react';
import { Check, ChevronDown } from 'lucide-react';

/**
 * CMultiSelect - shared multi-value counterpart of the custom `Dropdown`.
 *
 * Same trigger and menu styling as Dropdown, so a filter bar mixing the two
 * reads as one control set. The menu stays open while options are toggled;
 * Escape, Tab or a click outside closes it.
 *
 * Accessibility follows the WAI-ARIA listbox pattern: focus stays on the
 * trigger, which points at the highlighted option through
 * aria-activedescendant, and the listbox is aria-multiselectable. Keyboard:
 * Enter / Space / ArrowDown / ArrowUp open; ArrowUp / ArrowDown / Home / End
 * move; Enter or Space toggles; Escape closes.
 *
 * @param {object}   props
 * @param {Array<string|number>} [props.value=[]]  Selected values.
 * @param {Function} props.onChange      Called with the next array of values, in
 *   option order, so the same selection always serialises the same way.
 * @param {Array}    [props.options=[]]  [{ value, label }]
 * @param {string}   [props.placeholder='All'] Trigger text when nothing is selected.
 * @param {string}   [props.id]
 * @param {boolean}  [props.disabled=false]
 * @param {string}   [props['aria-label']]
 * @param {string}   [props['data-testid']]  On the trigger; each option gets
 *   `${testid}-option-${value}`.
 */
const CMultiSelect = React.forwardRef(({
  value = [],
  onChange,
  options = [],
  placeholder = 'All',
  id,
  disabled = false,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const clearRef = useRef(null);
  const listboxId = useId();

  useImperativeHandle(ref, () => buttonRef.current);

  const selectedValues = (value || []).map(String);
  const selected = new Set(selectedValues);
  const selectedOptions = options.filter((opt) => selected.has(String(opt.value)));
  // Values with no option to name them: the options have not loaded yet, their
  // fetch failed, or the record behind one was deleted. They still filter, so
  // the trigger must not read as an empty selection.
  const unlabelledCount = selectedValues.length - selectedOptions.length;
  const optionId = (index) => `${listboxId}-option-${index}`;
  const valueId = `${listboxId}-value`;

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const openMenu = () => {
    // With nothing to pick, the menu still opens while something is selected,
    // because it is where the selection is cleared.
    if (disabled || (!options.length && !selectedValues.length)) return;
    setIsOpen(true);
    const firstSelected = options.findIndex((opt) => selected.has(String(opt.value)));
    setHighlightedIndex(firstSelected >= 0 ? firstSelected : Math.min(0, options.length - 1));
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex];
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  const toggle = (opt) => {
    if (!onChange) return;
    const key = String(opt.value);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    // Unlabelled values are kept: toggling one option must not silently drop a
    // filter the user cannot see. Clear selection removes them.
    const known = new Set(options.map((o) => String(o.value)));
    onChange([
      ...options.filter((o) => next.has(String(o.value))).map((o) => String(o.value)),
      ...selectedValues.filter((v) => !known.has(v)),
    ]);
  };

  const focusTrigger = () => {
    if (buttonRef.current) buttonRef.current.focus();
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (!isOpen) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        openMenu();
      }
      return;
    }
    const last = options.length - 1;
    if (last < 0 && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((i) => (i >= last ? 0 : i + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((i) => (i <= 0 ? last : i - 1));
        break;
      case 'Home':
        event.preventDefault();
        setHighlightedIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setHighlightedIndex(last);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (options[highlightedIndex]) toggle(options[highlightedIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu();
        break;
      case 'Tab':
        // Focus stays on the trigger while the list is navigated, so Tab is
        // the way into the popup's Clear selection button.
        if (!event.shiftKey && clearRef.current) {
          event.preventDefault();
          clearRef.current.focus();
        } else {
          closeMenu();
        }
        break;
      default:
        break;
    }
  };

  const handleClearKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      focusTrigger();
    } else if (event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      focusTrigger();
    } else if (event.key === 'Tab') {
      closeMenu();
    }
  };

  const handleClick = () => {
    if (disabled) return;
    if (isOpen) closeMenu();
    else openMenu();
  };

  const clearAll = () => {
    if (onChange) onChange([]);
    // The button goes away with the selection; keep focus in the control.
    focusTrigger();
  };

  const baseClasses = 'relative w-full border border-gray-300 rounded-md pl-3 pr-9 py-2 bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
  const stateClasses = disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'cursor-pointer';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        ref={buttonRef}
        id={id}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`${baseClasses} ${stateClasses}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen && options.length ? listboxId : undefined}
        aria-activedescendant={isOpen && highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined}
        aria-label={ariaLabel}
        // aria-label names the field, which hides the button's own text from
        // the accessible name; the description is what announces the selection.
        aria-describedby={valueId}
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      >
        <span id={valueId} className="flex min-w-0 items-center gap-2">
          {selectedValues.length === 0 && (
            <span className="block truncate text-gray-500">{placeholder}</span>
          )}
          {unlabelledCount > 0 && (
            <span className="block truncate text-gray-900">{`${selectedValues.length} selected`}</span>
          )}
          {selectedValues.length > 0 && unlabelledCount === 0 && (
            <>
              <span className="block truncate text-gray-900">{selectedOptions[0].label}</span>
              {selectedOptions.length > 1 && (
                <span className="shrink-0 rounded-full bg-primary-50 px-2 text-xs font-medium leading-5 text-primary-700">
                  +{selectedOptions.length - 1}
                  <span className="sr-only"> more</span>
                </span>
              )}
            </>
          )}
        </span>
        <ChevronDown
          className={`pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
          {options.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-500">No options</p>
          )}
          {options.length > 0 && (
            <ul
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-multiselectable="true"
              aria-label={ariaLabel}
              className="max-h-60 overflow-auto py-1 focus:outline-none"
            >
              {options.map((opt, index) => {
                const isSelected = selected.has(String(opt.value));
                const isHighlighted = index === highlightedIndex;
                return (
                  <li
                    key={String(opt.value)}
                    id={optionId(index)}
                    role="option"
                    aria-selected={isSelected}
                    className={`flex cursor-pointer select-none items-center gap-2 px-3 py-2 text-sm ${
                      isHighlighted ? 'bg-gray-100' : 'hover:bg-gray-50'
                    } ${isSelected ? 'font-medium text-primary-900' : 'text-gray-900'}`}
                    {...(dataTestId ? { 'data-testid': `${dataTestId}-option-${opt.value}` } : {})}
                    onMouseDown={(event) => {
                      // Keeps focus on the trigger, as Dropdown does.
                      event.preventDefault();
                      toggle(opt);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        isSelected ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white'
                      }`}
                      aria-hidden="true"
                    >
                      {isSelected && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {selectedValues.length > 0 && (
            <div className="border-t border-gray-100 px-3 py-2">
              <button
                type="button"
                ref={clearRef}
                // mousedown would move focus off the trigger before the click lands.
                onMouseDown={(event) => event.preventDefault()}
                onClick={clearAll}
                onKeyDown={handleClearKeyDown}
                className={'rounded text-xs font-medium text-primary-600 hover:text-primary-800'
                  + ' focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'}
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

CMultiSelect.displayName = 'CMultiSelect';

export default CMultiSelect;
